import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiSave, FiUser, FiMail, FiPhone, FiMapPin, FiCamera,
  FiShield, FiKey, FiEye, FiEyeOff, FiX, FiCreditCard, FiCalendar, FiStar, FiZap, FiAward
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import styles from './Profile.module.css';

interface ProfileForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  taxId: string;
  profilePicture?: File;
}

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}



export function Profile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Debug logging
  console.log('Profile component - user:', user);
  console.log('Profile component - user.subscription:', user?.subscription);
  
  // Add useEffect for debugging
  React.useEffect(() => {
    console.log('Profile component mounted');
    console.log('Profile component - user in useEffect:', user);
    console.log('Profile component - user.subscription in useEffect:', user?.subscription);
  }, [user]);
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isValid }
  } = useForm<ProfileForm>({
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      company: user?.company || '',
      address: {
        street: user?.address?.street || '',
        city: user?.address?.city || '',
        state: user?.address?.state || '',
        zipCode: user?.address?.zipCode || '',
        country: user?.address?.country || 'South Africa'
      },
      taxId: user?.taxId || ''
    }
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    watch: watchPassword,
    formState: { errors: passwordErrors, isValid: isPasswordValid }
  } = useForm<PasswordForm>();

  const watchedNewPassword = watchPassword('newPassword');

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      setValue('profilePicture', file);
    }
  };

  const onSubmit = async (data: ProfileForm) => {
    try {
      setIsSaving(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('Updated profile:', data);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const onPasswordSubmit = async (data: PasswordForm) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    try {
      setIsChangingPassword(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('Password changed:', data);
      toast.success('Password changed successfully!');
      setShowPasswordForm(false);
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleUpgradePlan = () => {
    navigate('/settings/subscription');
  };

  const handleManageBilling = () => {
    navigate('/settings/subscription');
  };

  return (
    <motion.div 
      className={styles.container} 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Profile Settings</h1>
          <p className={styles.subtitle}>Manage your personal information and account settings</p>
        </div>
      </div>

      <div className={styles.content}>
        {/* Profile Picture Section */}
        <motion.div 
          className={styles.section}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className={styles.sectionTitle}>
            <FiUser size={20} />
            Profile Picture
          </h2>
          
          <div className={styles.profilePictureSection}>
            <div className={styles.profilePicture}>
              {profileImage ? (
                <img src={profileImage} alt="Profile" className={styles.profileImage} />
              ) : user?.profilePicture ? (
                <img src={user.profilePicture} alt="Profile" className={styles.profileImage} />
              ) : (
                <div className={styles.profilePlaceholder}>
                  <FiUser size={48} />
                </div>
              )}
              <div className={styles.profileOverlay}>
                <label htmlFor="profile-upload" className={styles.uploadButton}>
                  <FiCamera size={20} />
                </label>
                <input
                  id="profile-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className={styles.hiddenInput}
                />
              </div>
            </div>
            <div className={styles.profileInfo}>
              <h3 className={styles.profileName}>{user?.firstName} {user?.lastName}</h3>
              <p className={styles.profileRole}>{user?.role}</p>
              <p className={styles.profileEmail}>{user?.email}</p>
            </div>
          </div>
        </motion.div>

        {/* Personal Information */}
        <motion.div 
          className={styles.section}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className={styles.sectionTitle}>
            <FiUser size={20} />
            Personal Information
          </h2>
          
          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>First Name *</label>
                <input
                  {...register('firstName', { required: 'First name is required' })}
                  className={`${styles.input} ${errors.firstName ? styles.error : ''}`}
                  placeholder="e.g., Thabo, Lerato, Sipho, Themba, Nkosi, Bongani, Nhlanhla, Siyabonga"
                />
                {errors.firstName && (
                  <span className={styles.errorMessage}>{errors.firstName.message}</span>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.label}>Last Name *</label>
                <input
                  {...register('lastName', { required: 'Last name is required' })}
                  className={`${styles.input} ${errors.lastName ? styles.error : ''}`}
                  placeholder="e.g., Mokoena, Ndlovu, Zulu, Dlamini, Khumalo, Mabaso, Nkosi, Ndlovu"
                />
                {errors.lastName && (
                  <span className={styles.errorMessage}>{errors.lastName.message}</span>
                )}
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Email Address *</label>
                <div className={styles.inputWrapper}>
                  <FiMail className={styles.inputIcon} />
                  <input
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    className={`${styles.input} ${errors.email ? styles.error : ''}`}
                    placeholder="e.g., thabo@fastron.co.za, admin@company.co.za, info@business.co.za, sales@enterprise.co.za, contact@startup.co.za, hello@corporate.co.za, support@business.co.za"
                  />
                </div>
                {errors.email && (
                  <span className={styles.errorMessage}>{errors.email.message}</span>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.label}>Phone Number</label>
                <div className={styles.inputWrapper}>
                  <FiPhone className={styles.inputIcon} />
                  <input
                    {...register('phone')}
                    className={styles.input}
                    placeholder="+27 (XX) XXX-XXXX"
                  />
                </div>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Company/Business</label>
                              <input
                  {...register('company')}
                  className={styles.input}
                  placeholder="e.g., Fastron Motor Spares, ABC Trading, XYZ Services, DEF Solutions, GHI Industries, JKL Manufacturing, MNO Logistics"
                />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>VAT Number</label>
                              <input
                  {...register('taxId')}
                  className={styles.input}
                  placeholder="e.g., 123456789 (10 digits, no spaces or dashes, VAT registered, SARS compliant, tax compliant)"
                />
            </div>
          </form>
        </motion.div>

        {/* Address Information */}
        <motion.div 
          className={styles.section}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className={styles.sectionTitle}>
            <FiMapPin size={20} />
            Address Information
          </h2>
          
          <div className={styles.formGroup}>
            <label className={styles.label}>Street Address</label>
            <input
              {...register('address.street')}
              className={styles.input}
              placeholder="e.g., 123 Commissioner Street, Braamfontein, Johannesburg, Gauteng, South Africa, 2001, Business District, CBD"
            />
          </div>

                      <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>City</label>
                <input
                  {...register('address.city')}
                  className={styles.input}
                  placeholder="e.g., Johannesburg, Cape Town, Durban, Pretoria, Port Elizabeth, Bloemfontein, Kimberley, East London, Nelspruit"
                />
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.label}>Province</label>
                <select
                  {...register('address.state')}
                  className={styles.select}
                >
                  <option value="">Select Province</option>
                  <option value="Gauteng">Gauteng</option>
                  <option value="Western Cape">Western Cape</option>
                  <option value="KwaZulu-Natal">KwaZulu-Natal</option>
                  <option value="Eastern Cape">Eastern Cape</option>
                  <option value="Free State">Free State</option>
                  <option value="Mpumalanga">Mpumalanga</option>
                  <option value="Limpopo">Limpopo</option>
                  <option value="North West">North West</option>
                  <option value="Northern Cape">Northern Cape</option>
                </select>
              </div>
            </div>

                      <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Postal Code</label>
                <input
                  {...register('address.zipCode')}
                  className={styles.input}
                  placeholder="e.g., 2001 (JHB), 8001 (CT), 4001 (DBN), 0001 (PTA), 9300 (BLO), 8300 (KIM), 5200 (EL)"
                />
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.label}>Country</label>
                <select
                  {...register('address.country')}
                  className={styles.select}
                >
                  <option value="South Africa">South Africa</option>
                  <option value="Botswana">Botswana</option>
                  <option value="Namibia">Namibia</option>
                  <option value="Zimbabwe">Zimbabwe</option>
                  <option value="Zambia">Zambia</option>
                  <option value="Mozambique">Mozambique</option>
                  <option value="Lesotho">Lesotho</option>
                  <option value="Eswatini">Eswatini</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
        </motion.div>

        {/* Security Section */}
        <motion.div 
          className={styles.section}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className={styles.sectionTitle}>
            <FiShield size={20} />
            Security Settings
          </h2>
          
          <div className={styles.securitySection}>
            <div className={styles.securityInfo}>
              <h3 className={styles.securityTitle}>Change Password</h3>
              <p className={styles.securityDescription}>
                Update your password to keep your account secure
              </p>
            </div>
            
            {!showPasswordForm ? (
              <button
                onClick={() => setShowPasswordForm(true)}
                className={styles.securityButton}
              >
                <FiKey size={16} />
                Change Password
              </button>
            ) : (
              <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className={styles.passwordForm}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Current Password</label>
                  <div className={styles.inputWrapper}>
                    <FiKey className={styles.inputIcon} />
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      {...registerPassword('currentPassword', { required: 'Current password is required' })}
                      className={`${styles.input} ${passwordErrors.currentPassword ? styles.error : ''}`}
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className={styles.passwordToggle}
                    >
                      {showCurrentPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                    </button>
                  </div>
                  {passwordErrors.currentPassword && (
                    <span className={styles.errorMessage}>{passwordErrors.currentPassword.message}</span>
                  )}
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>New Password</label>
                    <div className={styles.inputWrapper}>
                      <FiKey className={styles.inputIcon} />
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        {...registerPassword('newPassword', {
                          required: 'New password is required',
                          minLength: { value: 8, message: 'Password must be at least 8 characters' }
                        })}
                        className={`${styles.input} ${passwordErrors.newPassword ? styles.error : ''}`}
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className={styles.passwordToggle}
                      >
                        {showNewPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                      </button>
                    </div>
                    {passwordErrors.newPassword && (
                      <span className={styles.errorMessage}>{passwordErrors.newPassword.message}</span>
                    )}
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Confirm New Password</label>
                    <div className={styles.inputWrapper}>
                      <FiKey className={styles.inputIcon} />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        {...registerPassword('confirmPassword', {
                          required: 'Please confirm your password',
                          validate: value => value === watchedNewPassword || 'Passwords do not match'
                        })}
                        className={`${styles.input} ${passwordErrors.confirmPassword ? styles.error : ''}`}
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className={styles.passwordToggle}
                      >
                        {showConfirmPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                      </button>
                    </div>
                    {passwordErrors.confirmPassword && (
                      <span className={styles.errorMessage}>{passwordErrors.confirmPassword.message}</span>
                    )}
                  </div>
                </div>

                <div className={styles.passwordActions}>
                  <button
                    type="submit"
                    disabled={isChangingPassword || !isPasswordValid}
                    className={`${styles.saveButton} ${isChangingPassword ? styles.loading : ''}`}
                  >
                    <FiSave size={16} />
                    {isChangingPassword ? 'Changing...' : 'Change Password'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPasswordForm(false)}
                    className={styles.cancelButton}
                  >
                    <FiX size={16} />
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </motion.div>

        {/* Debug Info */}
        <motion.div 
          className={styles.section}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className={styles.sectionTitle}>
            Debug Info
          </h2>
          <div style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '0.5rem', fontFamily: 'monospace' }}>
            <p>User exists: {user ? 'Yes' : 'No'}</p>
            <p>User ID: {user?.id || 'N/A'}</p>
            <p>User email: {user?.email || 'N/A'}</p>
            <p>Subscription exists: {user?.subscription ? 'Yes' : 'No'}</p>
            <p>Subscription plan: {user?.subscription?.plan || 'N/A'}</p>
            <p>Subscription status: {user?.subscription?.status || 'N/A'}</p>
          </div>
        </motion.div>

        {/* Subscription Status Section */}
        {user?.subscription && (
          <motion.div 
            className={styles.section}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
          <h2 className={styles.sectionTitle}>
            <FiCreditCard size={20} />
            Subscription Status
          </h2>
          
          <div className={styles.subscriptionInfo}>
            <div className={styles.planCard}>
              <div className={styles.planHeader}>
                {user?.subscription?.plan === 'free' && <FiStar size={24} className={styles.planIcon} />}
                {user?.subscription?.plan === 'pro' && <FiZap size={24} className={styles.planIcon} />}
                {user?.subscription?.plan === 'business' && <FiAward size={24} className={styles.planIcon} />}
                <div className={styles.planDetails}>
                  <h3 className={styles.planName}>
                    {user?.subscription?.plan === 'free' && 'Free Plan'}
                    {user?.subscription?.plan === 'pro' && 'Professional Plan'}
                    {user?.subscription?.plan === 'business' && 'Business Plan'}
                  </h3>
                  <span className={`${styles.planStatus} ${styles[user?.subscription?.status || '']}`}>
                    {user?.subscription?.status === 'active' && 'Active'}
                    {user?.subscription?.status === 'canceled' && 'Canceled'}
                    {user?.subscription?.status === 'past_due' && 'Past Due'}
                    {user?.subscription?.status === 'unpaid' && 'Unpaid'}
                  </span>
                </div>
              </div>
              
              <div className={styles.billingInfo}>
                <div className={styles.billingRow}>
                  <FiCalendar size={16} />
                  <span>Current Period: {user?.subscription?.currentPeriodStart ? user.subscription.currentPeriodStart.toLocaleDateString() : 'N/A'} - {user?.subscription?.currentPeriodEnd ? user.subscription.currentPeriodEnd.toLocaleDateString() : 'N/A'}</span>
                </div>
                {user?.subscription?.cancelAtPeriodEnd && (
                  <div className={styles.cancelNotice}>
                    <span>⚠️ Subscription will end on {user?.subscription?.currentPeriodEnd ? user.subscription.currentPeriodEnd.toLocaleDateString() : 'N/A'}</span>
                  </div>
                )}
              </div>
              
              <div className={styles.subscriptionActions}>
                <button 
                  className={styles.upgradeButton}
                  onClick={handleUpgradePlan}
                >
                  <FiStar size={16} />
                  Upgrade Plan
                </button>
                <button 
                  className={styles.manageButton}
                  onClick={handleManageBilling}
                >
                  <FiCreditCard size={16} />
                  Manage Billing
                </button>
              </div>
            </div>
          </div>
        </motion.div>
        )}

        {/* No Subscription Message */}
        {!user?.subscription && (
          <motion.div 
            className={styles.section}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className={styles.sectionTitle}>
              <FiCreditCard size={20} />
              Subscription Status
            </h2>
            
            <div className={styles.subscriptionInfo}>
              <div className={styles.planCard}>
                <div className={styles.noSubscription}>
                  <FiStar size={48} className={styles.noSubscriptionIcon} />
                  <h3 className={styles.noSubscriptionTitle}>No Active Subscription</h3>
                  <p className={styles.noSubscriptionDescription}>
                    You're currently on the free plan. Upgrade to unlock premium features and increase your business capabilities.
                  </p>
                  <button 
                    className={styles.upgradeButton}
                    onClick={handleUpgradePlan}
                  >
                    <FiStar size={16} />
                    View Plans
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Save Button */}
        <motion.div 
          className={styles.actions}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <button
            onClick={handleSubmit(onSubmit)}
            disabled={isSaving || !isValid}
            className={`${styles.saveButton} ${isSaving ? styles.loading : ''}`}
          >
            <FiSave size={16} />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
}
